import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ClipboardCheck, Save, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { SyntheseCandidatDialog } from '../bilan/SyntheseCandidatDialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (d) => { try { return format(parseISO(d), 'dd/MM/yyyy'); } catch { return d || ''; } };
const TYPE = { intermediaire: 'Bilan intermédiaire', final: 'Bilan final' };

// Synthèses individuelles du formateur telles que reprises dans chaque bilan de formation (éditables ici)
export const SynthesesBilansPanel = ({ dossierId, candidatId, candidatNom, canEdit }) => {
  const [items, setItems] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(null);
  const [ia, setIa] = useState(null); // bilan_id ciblé par l'insertion IA
  const load = () => axios.get(`${API_URL}/dossiers/${dossierId}/candidats/${candidatId}/syntheses-bilans`).then(r => { setItems(r.data); setEdits(Object.fromEntries(r.data.map(b => [b.bilan_id, b.synthese || '']))); }).catch(() => setItems([]));
  useEffect(() => { load(); }, [dossierId, candidatId]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (bilanId) => {
    setSaving(bilanId);
    try { await axios.put(`${API_URL}/dossiers/${dossierId}/candidats/${candidatId}/syntheses-bilans/${bilanId}`, { synthese: edits[bilanId] }); toast.success('Synthèse enregistrée dans le bilan'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } finally { setSaving(null); }
  };
  if (!items) return <Card className="border-0 shadow-sm"><CardContent className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></CardContent></Card>;
  return (
    <Card className="border-0 shadow-sm" data-testid="syntheses-bilans-panel">
      <CardContent className="p-5 space-y-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-indigo-500" /> Synthèse individuelle du formateur</h2>
          <p className="text-xs text-slate-400 mt-0.5">Reprise dans le bilan individuel (PDF) de chaque bilan de formation. Une synthèse par bilan.</p>
        </div>
        {items.length === 0 ? <p className="text-xs text-slate-400 italic" data-testid="syntheses-bilans-vide">Aucun bilan de formation créé pour cette action pour le moment.</p> : items.map(b => (
          <div key={b.bilan_id} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`synthese-bilan-${b.bilan_id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-xs font-semibold text-slate-700">{TYPE[b.type] || b.type} du {fmt(b.date_bilan)}{b.decision && <span className="ml-2 text-[10px] font-normal text-slate-400">décision : {b.decision}</span>}</p>
              {canEdit && (
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => setIa(b.bilan_id)} className="text-[11px] font-medium text-purple-700 inline-flex items-center gap-1 hover:underline px-2" data-testid={`synthese-bilan-ia-${b.bilan_id}`}><Sparkles className="w-3 h-3" /> Synthèse IA</button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" disabled={saving === b.bilan_id || (edits[b.bilan_id] || '') === (b.synthese || '')} onClick={() => save(b.bilan_id)} data-testid={`synthese-bilan-save-${b.bilan_id}`}><Save className="w-3 h-3 mr-1" /> Enregistrer</Button>
                </div>
              )}
            </div>
            <Textarea rows={3} value={edits[b.bilan_id] || ''} disabled={!canEdit} onChange={(e) => setEdits({ ...edits, [b.bilan_id]: e.target.value })} className="text-sm" placeholder="Implication, assiduité, progression, résultats, recommandations…" data-testid={`synthese-bilan-textarea-${b.bilan_id}`} />
          </div>
        ))}
        <SyntheseCandidatDialog open={!!ia} onClose={() => setIa(null)} dossierId={dossierId} candidatId={candidatId} candidatNom={candidatNom}
          onInsert={(t) => setEdits(e => ({ ...e, [ia]: [t, e[ia]].filter(Boolean).join('\n\n') }))} />
      </CardContent>
    </Card>
  );
};
